/**
 * Every item — the first layer of Settings → Readiness.
 *
 * What every item in the workspace must have, whatever it is being used for
 * (Completeness, canon 24 §3). Each field is Optional, Warning or Hard Stop.
 * Saved to workspace_definition_policy; loaded for everyone by
 * ReadinessRulesBootstrap; read through lib/itemReadiness.ts by every screen
 * that asks whether an item is complete. The second layer, "Ready for ranking",
 * includes every Hard Stop set here.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Filter,
  Info,
  RefreshCw,
  RotateCcw,
} from 'lucide-react';
import { useDefinitionPolicyStore } from '../../../stores/prioritization/definitionPolicyStore';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import {
  STRUCTURAL_FIELD_METAS,
  type DefinitionBehavior,
  type FieldDefinitionMeta,
} from '../../../types/prioritization/definitionPolicy';
import type { Attribute } from '../../../types/prioritization';

// ─── Behavior toggle ────────────────────────────────────────────────────────

const BEHAVIORS: Array<{
  value: DefinitionBehavior;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  activeClass: string;
  inactiveClass: string;
  description: string;
}> = [
  {
    value: 'optional',
    label: 'Optional',
    icon: CheckCircle2,
    activeClass: 'bg-[var(--hover-tint)] text-[var(--text-sec)] border-[var(--rule)]',
    inactiveClass: 'text-[var(--text-ter)] border-[var(--rule)] hover:bg-[var(--surface)]',
    description: 'Not checked. Never shows as missing.',
  },
  {
    value: 'warn',
    label: 'Warning',
    icon: AlertTriangle,
    activeClass: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]',
    inactiveClass: 'text-[var(--text-ter)] border-[var(--rule)] hover:bg-[var(--mustard-dim)] hover:text-[var(--mustard)]',
    description: 'Shows as missing on the item and in Needs Definition. Blocks nothing.',
  },
  {
    value: 'block',
    label: 'Hard Stop',
    icon: Ban,
    activeClass: 'bg-[var(--coral-dim)] text-[var(--coral)] border-[var(--coral)]',
    inactiveClass: 'text-[var(--text-ter)] border-[var(--rule)] hover:bg-[var(--coral-dim)] hover:text-[var(--coral)]',
    description: 'The item is incomplete until it is filled in: it cannot join a pool or be ranked.',
  },
];

function BehaviorToggle({
  fieldKey: _fieldKey,
  currentBehavior,
  alwaysBlock,
  onChange,
}: {
  fieldKey: string;
  currentBehavior: DefinitionBehavior;
  alwaysBlock?: boolean;
  onChange: (behavior: DefinitionBehavior) => void;
}) {
  if (alwaysBlock) {
    return (
      <div className="flex items-center gap-1.5 border border-[var(--coral)] bg-[var(--coral-dim)] px-3 py-1.5 text-sm font-medium text-[var(--coral)]">
        <Ban className="h-3.5 w-3.5" />
        Always required
      </div>
    );
  }

  return (
    <div className="flex border border-[var(--rule)] overflow-hidden" role="group">
      {BEHAVIORS.map((b, i) => {
        const Icon = b.icon;
        const isActive = currentBehavior === b.value;
        return (
          <button
            key={b.value}
            type="button"
            onClick={() => onChange(b.value)}
            title={b.description}
            className={[
              'flex items-center gap-1.5 border-0 px-3 py-1.5 text-sm font-medium transition-colors',
              i > 0 ? 'border-l border-[var(--rule)]' : '',
              isActive ? b.activeClass : b.inactiveClass,
            ].join(' ')}
          >
            <Icon className="h-3.5 w-3.5" />
            {b.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Field row ───────────────────────────────────────────────────────────────

function FieldRow({
  fieldKey,
  label,
  description,
  isFilter,
  currentBehavior,
  defaultBehavior,
  alwaysBlock,
  onChangeBehavior,
  onReset,
}: {
  fieldKey: string;
  label: string;
  description: string;
  isFilter?: boolean;
  currentBehavior: DefinitionBehavior;
  defaultBehavior: DefinitionBehavior;
  alwaysBlock?: boolean;
  onChangeBehavior: (behavior: DefinitionBehavior) => void;
  onReset: () => void;
}) {
  const isOverridden = !alwaysBlock && currentBehavior !== defaultBehavior;

  return (
    <div className="flex flex-col gap-3 border-b border-[var(--rule)] py-4 last:border-0 sm:flex-row sm:items-center sm:gap-4">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-[var(--text-pri)]">{label}</span>
          {isFilter && (
            <span
              className="flex items-center gap-1 rounded-full bg-[var(--slate-dim)] px-2 py-0.5 text-xs font-medium text-[var(--text-sec)]"
              title="Also available as a filter on the Items list"
            >
              <Filter className="h-3 w-3" />
              Filter
            </span>
          )}
          {isOverridden && (
            <span className="rounded-full bg-[var(--mustard-dim)] px-2 py-0.5 text-xs font-medium text-[var(--mustard)]">
              Overridden
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs text-[var(--text-ter)]">{description}</p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <BehaviorToggle
          fieldKey={fieldKey}
          currentBehavior={currentBehavior}
          alwaysBlock={alwaysBlock}
          onChange={onChangeBehavior}
        />
        {isOverridden && (
          <button
            type="button"
            onClick={onReset}
            title="Reset to default"
            className="p-1.5 text-[var(--text-ter)] hover:bg-[var(--hover-tint)] hover:text-[var(--text-sec)]"
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Section card ─────────────────────────────────────────────────────────────

function SectionCard({
  title,
  description,
  children,
  defaultOpen = true,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between p-5 text-left"
      >
        <div>
          <h2 className="text-base font-semibold text-[var(--text-pri)]">{title}</h2>
          <p className="mt-0.5 text-sm text-[var(--text-ter)]">{description}</p>
        </div>
        {open ? (
          <ChevronDown className="h-5 w-5 shrink-0 text-[var(--text-ter)]" />
        ) : (
          <ChevronRight className="h-5 w-5 shrink-0 text-[var(--text-ter)]" />
        )}
      </button>

      {open && <div className="border-t border-[var(--rule)] px-5">{children}</div>}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function EveryItemRulesSection() {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const userId = useUserStore((s) => s.userId);

  const { loading, error, overrides, loadForWorkspace, setFieldBehavior, resetField, getBehavior } =
    useDefinitionPolicyStore();
  const attributes = useAttributesStore((s) => s.attributes);
  const attributesLoading = useAttributesStore((s) => s.syncState.isSyncing);

  const [saving, setSaving] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [, setSaveSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (workspaceId) loadForWorkspace(workspaceId);
  }, [workspaceId, loadForWorkspace]);

  // Group structural fields by section
  const classificationFields = useMemo(
    () => STRUCTURAL_FIELD_METAS.filter((m) => m.section === 'classification'),
    []
  );
  const contextFields = useMemo(
    () => STRUCTURAL_FIELD_METAS.filter((m) => m.section === 'context'),
    []
  );
  const relationshipFields = useMemo(
    () => STRUCTURAL_FIELD_METAS.filter((m) => m.section === 'relationships'),
    []
  );

  const handleChange = async (fieldKey: string, behavior: DefinitionBehavior) => {
    setSaving(fieldKey);
    setSaveError(null);
    setSaveSuccess(null);
    const result = await setFieldBehavior(fieldKey, behavior, userId ?? undefined);
    setSaving(null);
    if (result.error) {
      setSaveError(result.error);
    } else {
      setSaveSuccess(fieldKey);
      setTimeout(() => setSaveSuccess(null), 2000);
    }
  };

  const handleReset = async (fieldKey: string) => {
    setSaving(fieldKey);
    setSaveError(null);
    const result = await resetField(fieldKey);
    setSaving(null);
    if (result.error) setSaveError(result.error);
  };

  const overrideCount = Object.keys(overrides).filter((k) => k !== 'title').length;

  const renderStructuralRow = (meta: FieldDefinitionMeta) => (
    <FieldRow
      key={meta.fieldKey}
      fieldKey={meta.fieldKey}
      label={meta.label}
      description={meta.description}
      isFilter={meta.isFilter}
      currentBehavior={getBehavior(meta.fieldKey)}
      defaultBehavior={meta.defaultBehavior}
      alwaysBlock={meta.alwaysBlock}
      onChangeBehavior={(b) => handleChange(meta.fieldKey, b)}
      onReset={() => handleReset(meta.fieldKey)}
    />
  );

  const renderAttributeRow = (attr: Attribute) => {
    const fieldKey = `attr:${attr.key}`;
    const defaultBehavior: DefinitionBehavior = attr.required ? 'warn' : 'optional';
    return (
      <FieldRow
        key={fieldKey}
        fieldKey={fieldKey}
        label={attr.label}
        description={
          attr.description ??
          (attr.required
            ? 'Marked required in Attributes, so it warns when empty unless you make it a Hard Stop.'
            : 'Optional scoring attribute.')
        }
        isFilter={false}
        currentBehavior={getBehavior(fieldKey, { required: attr.required })}
        defaultBehavior={defaultBehavior}
        onChangeBehavior={(b) => handleChange(fieldKey, b)}
        onReset={() => handleReset(fieldKey)}
      />
    );
  };

  return (
    <div className="space-y-6">
      {(loading || overrideCount > 0) && (
        <div className="flex items-center gap-3">
          {loading && (
            <span className="flex items-center gap-1.5 text-sm text-[var(--text-ter)]">
              <RefreshCw className="h-4 w-4 animate-spin" />
              Loading…
            </span>
          )}
          {overrideCount > 0 && (
            <span className="rounded-full bg-[var(--mustard-dim)] px-3 py-1 text-sm font-medium text-[var(--mustard)]">
              {overrideCount} changed from the default
            </span>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-5">
        <h3 className="mb-3 text-sm font-semibold text-[var(--text-sec)]">Behavior key</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {BEHAVIORS.map((b) => {
            const Icon = b.icon;
            return (
              <div key={b.value} className="flex items-start gap-2.5">
                <div className={`mt-0.5 rounded p-1 ${b.activeClass}`}>
                  <Icon className="h-3.5 w-3.5" />
                </div>
                <div>
                  <div className="text-sm font-medium text-[var(--text-pri)]">{b.label}</div>
                  <div className="text-xs text-[var(--text-ter)]">{b.description}</div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 border border-[var(--slate)] bg-[var(--slate-dim)] p-3 text-sm text-[var(--text-sec)]">
          <span className="font-medium">Filters:</span> Fields marked{' '}
          <span className="inline-flex items-center gap-1 rounded-full bg-[var(--slate-dim)] px-1.5 text-xs font-medium text-[var(--text-sec)]">
            <Filter className="h-3 w-3" />
            Filter
          </span>{' '}
          can also be filtered on in the Items list.
        </div>
      </div>

      {error && (
        <div className="border border-[var(--coral)] bg-[var(--coral-dim)] p-4 text-sm text-[var(--coral)]">
          Could not load the every-item rules: {error}
        </div>
      )}

      {saveError && (
        <div className="border border-[var(--coral)] bg-[var(--coral-dim)] p-4 text-sm text-[var(--coral)]">
          Save failed: {saveError}
        </div>
      )}

      {/* Classification */}
      <SectionCard
        title="Classification"
        description="Core fields that describe what an item is and how it fits within the system."
      >
        {classificationFields.map(renderStructuralRow)}
      </SectionCard>

      {/* Context */}
      <SectionCard
        title="Context & Ownership"
        description="Fields that provide the 'why' and 'who' behind an item."
        defaultOpen={false}
      >
        {contextFields.map(renderStructuralRow)}
      </SectionCard>

      {/* Relationships */}
      <SectionCard
        title="Relationships"
        description="Structural links to other items in the hierarchy."
        defaultOpen={false}
      >
        {relationshipFields.map(renderStructuralRow)}
      </SectionCard>

      {/* Scoring Attributes */}
      <SectionCard
        title="Scoring Attributes"
        description="Attributes used to score and compare items. One marked required in Attributes starts as a Warning here. A value accepted in a session counts."
        defaultOpen={true}
      >
        {attributesLoading && (
          <div className="flex items-center gap-2 py-4 text-sm text-[var(--text-ter)]">
            <RefreshCw className="h-4 w-4 animate-spin" />
            Loading attributes…
          </div>
        )}
        {!attributesLoading && attributes.length === 0 && (
          <div className="py-6 text-center text-sm text-[var(--text-ter)]">
            No scoring attributes yet.{' '}
            <a
              href="/prioritization/settings/attributes"
              className="font-medium text-[var(--text-pri)] underline"
            >
              Add attributes →
            </a>
          </div>
        )}
        {!attributesLoading && attributes.map(renderAttributeRow)}
      </SectionCard>

      {/* Where the rules apply */}
      <div className="border border-[var(--rule)] bg-[var(--surface)] p-4">
        <div className="flex items-start gap-2.5 text-sm text-[var(--text-sec)]">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--text-ter)]" />
          <div>
            <p className="font-medium text-[var(--text-sec)]">Where these rules apply, for everyone, as soon as you change them</p>
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              <li>The item page: a Hard Stop shows as Required, a Warning as Missing, under Needs attention.</li>
              <li>Items › Needs Definition and its count: items missing a Warning or Hard Stop about their own description (values are not counted there).</li>
              <li>Items › Completeness and the Items list: each field marked Required or Recommended, and the Complete filter.</li>
              <li>Adding an item to a pool: every Hard Stop must be met.</li>
              <li>Ready for ranking, below: every Hard Stop is part of every readiness definition.</li>
              <li>The Pipeline Health report.</li>
            </ul>
          </div>
        </div>
      </div>

      {saving && (
        <div className="fixed bottom-4 right-4 flex items-center gap-2 border border-[var(--rule)] bg-[var(--bg-raised)] px-4 py-2.5 shadow-lg text-sm text-[var(--text-sec)]">
          <RefreshCw className="h-4 w-4 animate-spin text-[var(--text-ter)]" />
          Saving…
        </div>
      )}
    </div>
  );
}
